import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

// @mui
import { useTheme } from '@mui/material/styles';
import { Grid, Container, Typography, FormControl, Select, MenuItem, CardContent, Card, Box, Tabs, Tab, Skeleton } from '@mui/material';
// components
import Iconify from '../components/iconify';
// sections
import {
  AppCurrentVisits,
  AppWebsiteVisits,
  AppWidgetSummary
} from '../sections/@dashboard/app';
import { getInterpreterDashboard, getDashboardTrends, getDashboardGroupChart, getAverageTurnaroundTime, getExpertAnalysisTime } from 'api/dashboard';
import { useSnackbar } from '../components/snackbar'
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ScienceIcon from '@mui/icons-material/Science';
// ----------------------------------------------------------------------

export default function DashboardAppPage() {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [pieLoading, setPieLoading] = useState(true);
  const [allCount, setAllCount] = useState({ todayCompleted: 0, todayRequested: 0, totalCompleted: 0, totalPending: 0 });
  const [trends, setTrends] = useState([]);
  const [groupData, setGroupData] = useState([]);
  const { todayCompleted, todayRequested, totalCompleted, totalPending, totalReports } = allCount;

  const [allExpertsTotal, setAllExpertsTotal] = useState(0);
  const [selectedRange, setSelectedRange] = useState('lastMonth');
  const [tatData, setTatData] = useState(null);
  const [tatLoading, setTatLoading] = useState(true);

  const [selectTATRange, setSelectTATRange] = useState('lastWeek');
  const [expertAnalysisData, setExpertAnalysisData] = useState([]);
  const [expertLoading, setExpertLoading] = useState(true);
   const [tatTab, setTatTab] = useState(0);

useState({
  todayCompleted: 0,
  todayRequested: 0,
  totalCompleted: 0,
  totalPending: 0,
  totalReports: 0
});

  useEffect(() => {
    fetchData();
    fetchTrendsData();
  }, []);

  useEffect(() => {
    fetchPieChartData(selectedRange);
  }, [selectedRange]);

  useEffect(() => {
    fetchTurnaroundData(selectTATRange);
    fetchExpertAnalysisData(selectTATRange);
  }, [selectTATRange, tatTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getInterpreterDashboard({});
      setLoading(false);
      if (response && response.success) {
        if (response.data) {
          const dt = response.data;
          setAllCount(dt);
        }
      } else {
        if (response && response.message) {
          enqueueSnackbar(response.message, { variant: 'error' })
        }
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const fetchTrendsData = async () => {
    try {
      const response = await getDashboardTrends({});
      setTrendsLoading(false);
      if (response && response.success) {
        setTrends(response?.data || []);
      }
    } catch (error) {
      setTrendsLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const fetchPieChartData = async (range) => {
    try {
      setPieLoading(true);
      const response = await getDashboardGroupChart({ range });
      setPieLoading(false);
      if (response?.success && response?.data) {
        setAllExpertsTotal(response.data.All || 0);
        const mapGroupChartData = Object.keys(response.data)
          .filter((key) => key !== "All") // exclude the "All" key
          .map((key) => ({
            label: key,
            value: response.data[key],
          }));
        setGroupData(mapGroupChartData);
      }
    } catch (error) {
      setPieLoading(false);
      console.error("Error fetching group chart data:", error);
    }
  };
  const fetchTurnaroundData = async (range) => {
    try {
      setTatLoading(true);
      let response;
      const reportWR = true;
      setTatData(null);
      if (tatTab === 1) {
        response = await getAverageTurnaroundTime({ range, reportWR });
      } else {
        response = await getAverageTurnaroundTime({ range });
      }
      setTatLoading(false);
      if (response?.success && response?.data) setTatData(response.data);
    } catch (error) {
      setTatLoading(false);
      console.error("Error fetching turnaround time:", error);
    }
  };
  const fetchExpertAnalysisData = async (range) => {
    try {
      setExpertLoading(true);
      let response;
      const reportWR = true;
      setExpertAnalysisData([]);
      if (tatTab === 1) {
        response = await getExpertAnalysisTime({ range, reportWR });
      } else {
        response = await getExpertAnalysisTime({ range });
      }
      setExpertLoading(false);
      if (response?.success && response?.data) {
        setExpertAnalysisData(response.data);
      } else {
        setExpertAnalysisData([]);
      }
    } catch (error) {
      setExpertLoading(false);
      console.error("Error fetching expert analysis data:", error);
    }
  };

  return (
    <>
      <Helmet>
        <title> {`Dashboard | ${process.env.REACT_APP_PORTAL_NAME}`}</title>
      </Helmet>

      <Container maxWidth="xl">
        {/* <Typography variant="h4" sx={{ mb: 5 }}>
          Hi, Welcome back
        </Typography> */}


        <Grid container spacing={3}>
          {totalReports ? (
            <>
              <Grid item xs={12} sm={4} md={2}>
                <AppWidgetSummary title="Today's total tests" total={totalReports || 0} loading={loading} color="info" icon={'healthicons:i-documents-accepted'} />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <AppWidgetSummary title="Today's report" total={todayRequested || 0} loading={loading} icon={'solar:documents-bold-duotone'} />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <AppWidgetSummary title="Interpreted by me" total={todayCompleted || 0} loading={loading} color="info" icon={'healthicons:i-documents-accepted'} />
              </Grid>
            </>) : (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <AppWidgetSummary title="Today's report" total={todayRequested || 0} loading={loading} icon={'solar:documents-bold-duotone'} />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <AppWidgetSummary title="Interpreted by me" total={todayCompleted || 0} loading={loading} color="info" icon={'healthicons:i-documents-accepted'} />
              </Grid>
            </>
          )
          }
          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary title="Interpreted in last 30 days" total={totalCompleted || 0} loading={loading} color="warning" icon={'uiw:document'} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary title="Pending in last 30 days" total={totalPending || 0} loading={loading} color="error" icon={'arcticons:onlyoffice-documents'} />
          </Grid>


          <Grid item xs={12} md={6} lg={8}>
            <AppWebsiteVisits
              title="Monthly Trends"
              // subheader="(+43%) than previous day"
              loading={trendsLoading}
              chartLabels={trends?.map(item => item?.month)}
              chartData={[
                {
                  name: 'Completed by Me',
                  type: 'column',
                  fill: 'gradient',
                  data: trends?.map(item => item?.completed_by_me),
                },
                {
                  name: 'Completed',
                  type: 'area',
                  fill: 'gradient',
                  data: trends?.map(item => item?.completed),
                },
                {
                  name: 'Pending',
                  type: 'line',
                  fill: 'solid',
                  data: trends?.map(item => item?.pending),
                },
              ]}
            />
          </Grid>
          {/* <Grid item xs={12} md={6} lg={4}>
            <AppCurrentVisits
              title="Expert-wise Completed"
              chartData={groupData}
              loading={ pieLoading }
              chartColors={[
                theme.palette.info.main,
                theme.palette.warning.main,
                theme.palette.error.main,
              ]}
            />
          </Grid> */}
          <Grid item xs={12} md={6} lg={4}>

            <AppCurrentVisits
              title="Expert-wise Completed"
              subheader={
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 120,
                    '& .MuiOutlinedInput-root': { height: 32 },
                    '& .MuiSelect-select': { py: 0.5, fontSize: 13 },
                  }}
                >
                  <Select
                    value={selectedRange}
                    onChange={(e) => setSelectedRange(e.target.value)}
                  >
                    <MenuItem value="lastWeek">Last 7 days</MenuItem>
                    <MenuItem value="lastMonth">Last 30 days</MenuItem>
                  </Select>
                </FormControl>
              }
              loading={pieLoading}
              chartData={groupData}
              chartColors={[
                theme.palette.info.main,
                theme.palette.warning.main,
                theme.palette.error.main,
              ]}
              totalLabel={`Total Reports: ${allExpertsTotal}`}
            />

          </Grid>
           <Grid item xs={12}>
            <Card sx={{ mt: 3, p: 0, borderRadius: 3, boxShadow: 2, minHeight: 450 }}>
              <CardContent>

                {/* Header */}
                <Grid container justifyContent="space-between" alignItems="center">
                  <Grid item>
                    <Tabs
                      value={tatTab}
                      onChange={(e, newValue) => {
                        setTatTab(newValue);
                        // fetchTurnaroundData(selectTATRange, newValue);
                      }}
                      textColor="primary"
                      indicatorColor="primary"
                    >
                      <Tab label="Turnaround Time" />
                      <Tab label="Reports Without Request" />
                    </Tabs>
                  </Grid>

                  <Grid item>
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 120,
                        '& .MuiOutlinedInput-root': { height: 32 },
                        '& .MuiSelect-select': { py: 0.5, fontSize: 13 },
                      }}
                    >
                      <Select
                        value={selectTATRange}
                        onChange={(e) => {
                          setSelectTATRange(e.target.value);
                          // fetchTurnaroundData(e.target.value, tatTab);
                        }}
                      >
                        <MenuItem value="lastWeek">This Week</MenuItem>
                        <MenuItem value="lastMonth">This Month</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Content */}
                {tatLoading ? (
                  <>
                    {/* Summary cards skeleton */}
                    <Grid container spacing={3} sx={{ mt: 1, mb: 3 }}>
                      {[1, 2, 3].map((_, idx) => (
                        <Grid item xs={12} md={4} key={idx}>
                          <Card
                            sx={{
                              p: 1.5,
                              display: "flex",
                              alignItems: "center",
                              bgcolor: "grey.50",
                              borderRadius: 2,
                              minHeight: 84,
                            }}
                          >
                            {/* Icon placeholder */}
                            <Skeleton variant="circular" width={36} height={36} sx={{ mr: 2 }} />

                            {/* Text placeholder */}
                            <Box sx={{ flex: 1 }}>
                              <Skeleton
                                variant="text"
                                sx={{ fontSize: "0.875rem", lineHeight: 1.4 }}
                              />
                              <Skeleton
                                variant="text"
                                sx={{ fontSize: "1.25rem", lineHeight: 1.6, width: "40%" }}
                              />

                            </Box>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Experts list skeleton */}
                    <Grid container sx={{ mt: 3, minHeight: 180 }}>
                      <Grid item xs={12}>
                        <Skeleton variant="text" width="30%" height={28} sx={{ mb: 1 }} />

                        {[1, 2, 3].map((_, idx) => (
                          <Card
                            key={idx}
                            sx={{
                              mb: 1,
                              p: 1.5,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              bgcolor: "grey.50",
                              borderRadius: 2,
                            }}
                          >
                            <Skeleton variant="text" width="40%" height={20} />
                            <Skeleton variant="text" width="20%" height={20} />
                          </Card>
                        ))}
                      </Grid>
                    </Grid>
                  </>
                ) : tatData ? (

                  <>
                    <Grid container spacing={3} sx={{ mt: 1, mb: 3 }}>
                      {/* Total */}
                      <Grid item xs={12} md={4}>
                        <Card sx={{ p: 1.5, display: "flex", alignItems: "center", bgcolor: "grey.50", borderRadius: 2 }}>
                          <AccessTimeIcon sx={{ color: "primary.main", fontSize: 36, mr: 2 }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Total Turnaround Time
                            </Typography>
                            <Typography variant="h6">
                              {tatData.readable?.averageTurnaround || "--"}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>

                      {/* Queue */}
                      <Grid item xs={12} md={4}>
                        <Card sx={{ p: 1.5, display: "flex", alignItems: "center", bgcolor: "grey.50", borderRadius: 2 }}>
                          <HourglassEmptyIcon sx={{ color: "primary.main", fontSize: 36, mr: 2 }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Queue Time
                            </Typography>
                            <Typography variant="h6">
                              {tatData.readable?.averageQueue || "--"}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>

                      {/* Analysis */}
                      <Grid item xs={12} md={4}>
                        <Card sx={{ p: 1.5, display: "flex", alignItems: "center", bgcolor: "grey.50", borderRadius: 2 }}>
                          <ScienceIcon sx={{ color: "primary.main", fontSize: 36, mr: 2 }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Analysis Time
                            </Typography>
                            <Typography variant="h6">
                              {tatData.readable?.averageAnalysis || "--"}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Experts */}
                    <Grid container sx={{ mt: 3 }}>
                      <Grid item xs={12}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          Analysis Time by Experts
                        </Typography>

                        {expertLoading ? (
                          <Skeleton variant="rectangular" width="100%" height={40} />
                        ) : expertAnalysisData.length > 0 ? (
                          expertAnalysisData.map((expert, idx) => (
                            <Card
                              key={idx}
                              sx={{
                                mb: 1,
                                p: 1.5,
                                display: "flex",
                                justifyContent: "space-between",
                                bgcolor: "grey.50",
                                borderRadius: 2,
                              }}
                            >
                              <Typography variant="body2">{expert.name}</Typography>
                              <Typography variant="body2" color="success.main">
                                {expert.readable?.averageAnalysis || "-"}
                              </Typography>
                            </Card>
                          ))
                        ) : (
                          <Typography variant="body2">No data available</Typography>
                        )}
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  <Typography variant="body2">No data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

        </Grid>

      </Container>
    </>
  );
}